import { FilterIcon, GroupIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import {
  FilterSubMenuDatePicker,
  FilterSubMenuMultiselect,
  Row,
  TableOrderByDropdown,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import {
  placementStatusOptions,
  placementTrackerSortOptions,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import {
  DateFilterOption,
  PlacementQueryParams,
  PlacementTrackerGroupBy,
} from "@/types";

// * IMPORTANT: groupByOptions values should be a column on the placement table
const groupByOptions = [
  { label: "Owner", value: PlacementTrackerGroupBy.Owner },
  { label: "Placement Status", value: PlacementTrackerGroupBy.Status },
];

export const PlacementTrackerTableQueryOptions = ({
  handleUpdateView,
  placementTrackerQueryParams,
}: {
  handleUpdateView: (placementTrackerQueryParams: PlacementQueryParams) => void;
  placementTrackerQueryParams: PlacementQueryParams;
}) => {
  const { userOptions } = useTaggableUsers();

  return (
    <Row className="items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="h-8" variant="outlineSecondary">
            <FilterIcon className="h-4 w-4" /> Filters
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[200px] p-0">
          <DropdownMenuLabel>Filters</DropdownMenuLabel>
          <DropdownMenuSeparator className="mb-0" />
          <FilterSubMenuMultiselect
            label="Owners"
            options={userOptions}
            selected={placementTrackerQueryParams.ownerIds}
            setSelected={(ownerIds) => {
              handleUpdateView({
                ...placementTrackerQueryParams,
                ownerIds,
              });
            }}
          />
          <FilterSubMenuMultiselect
            label="Placement Statuses"
            options={placementStatusOptions}
            selected={placementTrackerQueryParams.placementStatuses}
            setSelected={(statuses) => {
              handleUpdateView({
                ...placementTrackerQueryParams,
                placementStatuses: statuses,
              });
            }}
          />
          <FilterSubMenuDatePicker
            dateRange={placementTrackerQueryParams.dateAccepted}
            setDateRange={(dateRange?: DateRange) => {
              handleUpdateView({
                ...placementTrackerQueryParams,
                dateAccepted: dateRange,
                dateAcceptedFilter: DateFilterOption.Custom,
              });
            }}
            label="Date Accepted"
            selected={placementTrackerQueryParams.dateAcceptedFilter}
            setSelected={(dateAcceptedFilter: DateFilterOption) => {
              const dateRange = dates.handleDateRangeFilter(dateAcceptedFilter);
              handleUpdateView({
                ...placementTrackerQueryParams,
                dateAccepted: dateRange,
                dateAcceptedFilter: dateAcceptedFilter,
              });
            }}
          />
          <DropdownMenuItem
            className="p-0"
            onClick={() => {
              handleUpdateView({
                ...placementTrackerQueryParams,
                dateAccepted: undefined,
                dateAcceptedFilter: DateFilterOption.AllTime,
                ownerIds: [],
                placementStatuses: [],
              });
            }}
          >
            <DropdownMenuLabel className="font-medium">
              Clear all filters
            </DropdownMenuLabel>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="h-8" variant="outlineSecondary">
            <GroupIcon className="h-4 w-4" /> Group by
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="p-0">
          <DropdownMenuLabel>Grouping</DropdownMenuLabel>
          <DropdownMenuSeparator className="mb-0" />
          {groupByOptions.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.label}
              checked={placementTrackerQueryParams.groupBy === option.value}
              onClick={(e) => {
                e.preventDefault();
                handleUpdateView({
                  ...placementTrackerQueryParams,
                  groupBy:
                    placementTrackerQueryParams.groupBy === option.value
                      ? undefined
                      : option.value,
                });
              }}
              className="px-8"
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <TableOrderByDropdown
        queryParams={placementTrackerQueryParams}
        setQueryParams={handleUpdateView}
        sortOptions={placementTrackerSortOptions}
      />
    </Row>
  );
};
